import { useMemo, useCallback } from 'react';
import useModalState from '@sdk/components/ui/modal/useModalState';
import { useSession } from '@sdk/session/SessionContext';
import { PickerOption } from '@sdk/components/forms/types';

import { useNavigate } from '@web/Router/router.hooks';
import { HeaderActionOption } from '@web/conversations/Conversation/ConversationHeaderActions/conversationHeaderActions.types';
import { useConversationState } from '@web/conversations/Conversation/ConversationProvider/ConversationProvider';
import { useUnarchiveConversationAction } from '@web/conversations/Conversation/ConversationProvider/hooks/useUnarchiveConversationAction';
import { ConversationState } from '@web/conversations/Conversation/ConversationProvider/conversationProvider.types';
import { useMuteConversationAction } from '@web/conversations/Conversation/ConversationProvider/hooks/useMuteConversationAction';
import { useUnmuteConversationAction } from '@web/conversations/Conversation/ConversationProvider/hooks/useUnmuteConversationAction';

import { headerMenuOptions } from './conversationHeaderActions.consts';
import {
  canEditOrDeleteConversation,
  canLeaveConversation,
} from './conversationHeaderActions.utils';

export function useConversationHeaderActions() {
  const conversation = useConversationState();
  const menuOptions = useHeaderMenuOptions(conversation);
  const navigate = useNavigate();

  const deleteConversationModalState = useModalState();
  const leaveConversationModalState = useModalState();
  const archiveConversationModalState = useModalState();
  const exportConversationModalState = useModalState();

  const onClickActions = useMemo(
    () => ({
      export: exportConversationModalState.open,
      archive: archiveConversationModalState.open,
      delete: deleteConversationModalState.open,
      edit: () => navigate.toConversationEdit({ params: { id: conversation.id } }),
      leave: leaveConversationModalState.open,
    }),
    [
      conversation,
      deleteConversationModalState,
      leaveConversationModalState,
      archiveConversationModalState,
      exportConversationModalState,
      navigate,
    ]
  );
  const onClick = useOnClick(conversation.id, onClickActions);

  return {
    archiveConversationModalState,
    deleteConversationModalState,
    leaveConversationModalState,
    exportConversationModalState,
    menuOptions,
    onClick,
  };
}

type Actions = {
  export: () => void;
  archive: () => void;
  edit: () => void;
  delete: () => void;
  leave: () => void;
};
function useOnClick(conversationId: number, onClickActions: Actions) {
  const { action: unarchiveAction } = useUnarchiveConversationAction();
  const { action: muteAction } = useMuteConversationAction();
  const { action: unmuteAction } = useUnmuteConversationAction();

  const unarchiveConversation = useCallback(async () => {
    await unarchiveAction({ id: conversationId });
  }, [conversationId, unarchiveAction]);

  const muteConversation = useCallback(async () => {
    await muteAction({ id: conversationId });
  }, [conversationId, muteAction]);

  const unmuteConversation = useCallback(async () => {
    await unmuteAction({ id: conversationId });
  }, [conversationId, unmuteAction]);

  return useCallback(
    ({ key }: PickerOption) => {
      const actions = {
        [HeaderActionOption.EXPORT]: onClickActions.export,
        [HeaderActionOption.DELETE]: onClickActions.delete,
        [HeaderActionOption.EDIT]: onClickActions.edit,
        [HeaderActionOption.LEAVE]: onClickActions.leave,
        [HeaderActionOption.ARCHIVE]: onClickActions.archive,
        [HeaderActionOption.UNARCHIVE]: unarchiveConversation,
        [HeaderActionOption.MUTE]: muteConversation,
        [HeaderActionOption.UNMUTE]: unmuteConversation,
      };

      actions[key]?.();
    },
    [onClickActions, unarchiveConversation, unmuteConversation, muteConversation]
  );
}

function useHeaderMenuOptions(conversation: ConversationState) {
  const { currentUser } = useSession();

  return useMemo(() => {
    const flags = {
      [HeaderActionOption.EDIT]: canEditOrDeleteConversation(currentUser, conversation),
      [HeaderActionOption.EXPORT]: true,
      [HeaderActionOption.ARCHIVE]: !conversation.isArchived,
      [HeaderActionOption.UNARCHIVE]: conversation.isArchived,
      [HeaderActionOption.LEAVE]: canLeaveConversation(currentUser, conversation),
      [HeaderActionOption.DELETE]: canEditOrDeleteConversation(currentUser, conversation),
      [HeaderActionOption.MUTE]: !conversation.isSilenced,
      [HeaderActionOption.UNMUTE]: conversation.isSilenced,
    };

    return headerMenuOptions.filter((option) => flags[option.key]);
  }, [conversation, currentUser]);
}
